import { useQuery } from '@apollo/client';
import { CardCollectionById } from '@/clients/queries/contentful/CardCollectionById.graphql';
import { centeredMainGridClasses } from '@/styles/grid';
import { Heading, Subheading } from '../../../../packages/components/src/typography';
import typewriter, { Placement, Title } from '@/analytics/segment';
import Card from './Card';
import CardCarousel from './CardCarousel';
import SynCardCarousel from './SynCardCarousel';
import PackageCard from './PackageCard';
import SmallCard from './SmallCard';
import { motion } from 'framer-motion';
import CardCollectionLoading from './CardCollectionLoading';
import LayoutOne from './HeroCarouselLayouts/LayoutOne';
import { fetchContactFormById } from '@/clients/BentoBoxUtils';
import { isDoBusinessWithUsFormAtom } from '@/store';
import { useSetAtom } from 'jotai';
import ProductCardContainer from './Store/ProductCardContainer';
import Link from 'next/link';
import useSWR from 'swr';
import { Product } from '@/models/ProductTypes';
import { productPath } from '@/utils/productVariant';

import {
  CardCollectionFieldsFragment,
  CardCollectionByIdQuery,
  CardCollectionByIdQueryVariables,
  SubProductCardStore,
} from '../../@types/generated';
import { ImageAsset } from '@/models/ProductTypes';
import { Log } from '@/logging/datadog';
import { useEffect, useState } from 'react';
import { clsx } from 'clsx';

type Props = {
  data: CardCollectionFieldsFragment;
  headingSize?: 'xl' | 'large' | 'medium' | 'small' | 'xs';
  headingColor?: string;
  backgroundColor?: string;
  callback?: () => {};
};

const CARD_SPACE_BETWEEN = 22;

const isPackageCard = ({ data }: Props) => data.cardCollection?.items[0]?.__typename === 'PackageCard';

const packageCardBreakPoints = (numberOfCards: number) => {
  const slidesPerView1200 = () => {
    if (numberOfCards <= 3) {
      return 3;
    } else if (numberOfCards <= 4) {
      return 4;
    } else {
      return 4.3;
    }
  };

  return {
    0: {
      slidesPerView: 1.3,
      spaceBetween: CARD_SPACE_BETWEEN,
    },
    700: {
      slidesPerView: 2.3,
      spaceBetween: CARD_SPACE_BETWEEN,
    },
    1024: {
      slidesPerView: 3.3,
      spaceBetween: CARD_SPACE_BETWEEN,
    },
    1200: {
      slidesPerView: slidesPerView1200(),
      spaceBetween: CARD_SPACE_BETWEEN,
    },
  };
};

const cardsBreakPoints = (numberOfCards: number) => {
  return {
    700: {
      slidesPerView: 2.3,
      spaceBetween: CARD_SPACE_BETWEEN,
    },
    1024: {
      slidesPerView: numberOfCards <= 3 ? 3 : 3.3,
      spaceBetween: CARD_SPACE_BETWEEN,
    },
  };
};

const productListFetcher = async ([url, ids]: [string, Array<string>]): Promise<Array<Product>> => {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ productList: ids }),
  });
  if (!res.ok) return [];
  return ((await res.json()) || []) as Product[];
};

const RenderCarousel = ({
  data,
  backgroundColor = 'white-15',
  callback,
  isMobile = false,
}: Props & { isMobile?: boolean }) => {
  const _data = data;
  const productIds = (data.cardCollection?.items ?? [])
    .filter((card) => card?.__typename === 'SubProductCardStore')
    .map((card) => {
      if (card?.__typename === 'SubProductCardStore') {
        return card?.id;
      }
      return null;
    })
    .filter((id) => id !== null && id !== undefined) as string[];

  const { data: fetchedProducts, isLoading: productsLoading } = useSWR(
    productIds.length > 0 ? ['/api/store/product-list', productIds] : null,
    productListFetcher,
    { revalidateOnFocus: false, revalidateOnReconnect: false, dedupingInterval: 5000 }
  );
  const foundProductIds = new Set((fetchedProducts ?? []).map((p) => p.productId));

  const cards = (data.cardCollection?.items ?? [])
    .map((card) => {
      if (card?.__typename === 'SmallCard') {
        return <SmallCard key={card.id} data={card} />;
      } else if (card?.__typename === 'LargeCard') {
        return <Card key={card.id} card={card} onClick={callback} />;
      } else if (card?.__typename === 'PackageCard') {
        return <PackageCard key={card.id} data={card} hasDisclaimer={Boolean(data.disclaimer)} />;
      } else if (card?.__typename === 'BentoBox') {
        let data;
        if (_data.layout === 'large_three_box_no_scroll') {
          data = {
            cardHeight: '680px',
            cardWidth: '448px',
            items: [
              {
                sys: {
                  id: card.sys.id,
                },
              },
            ],
          };
        } else if (_data.layout === 'medium_three_box_no_scroll') {
          data = {
            cardHeight: '461px',
            cardWidth: '448px',
            items: [
              {
                sys: {
                  id: card.sys.id,
                },
              },
            ],
          };
        } else {
          data = {
            cardHeight: '461px',
            cardWidth: '328px',
            items: [
              {
                sys: {
                  id: card.sys.id,
                },
              },
            ],
          };
        }
        return <LayoutOne key={card.sys.id} data={data} />;
      } else if (card?.__typename === 'SubProductCardStore') {
        if (!card.id) return null;
        if (!productsLoading && !foundProductIds.has(card.id)) return null;
        return (
          <Link
            key={card.sys.id}
            href={productPath(card.id, card.id)}
            className="block w-full focus:outline-none focus:ring-2 focus:ring-offset-2"
          >
            <ProductCardContainer
              id={card.id}
              ids={productIds}
              images={(card as SubProductCardStore).imagesCollection?.items.map((i) => i?.image as ImageAsset)}
            />
          </Link>
        );
      }
      return null;
    })
    .filter(Boolean);

  // TODO: need to create a switchcase before this switchcase, that takes the layout into consideration
  if (data.layout === 'normal_box_horizontal_scroll') {
    return (
      <SynCardCarousel
        showShadows={!isMobile}
        cards={cards}
        disclaimer={data.disclaimer}
        isMobile={isMobile}
        breakpoints={isPackageCard({ data }) ? packageCardBreakPoints(cards.length) : cardsBreakPoints(cards.length)}
        backgroundColor={data.scrollGradientColor || backgroundColor || 'white-15'}
      />
    );
  } else if (data.layout === 'four_box_horizontal_scroll') {
    if (cards.length === 4) {
      return (
        <div className={clsx('col-span-full grid gap-[24px] md:grid-cols-4', { 'pb-12': isMobile })}>
          {cards.slice(0, 4).map((card, index) => (
            <div
              key={index}
              className="w-full"
              style={{
                borderRadius: '28px',
                overflow: isMobile ? 'visible' : 'hidden',
                boxShadow: isMobile
                  ? `0 12px 24px rgba(56, 4, 10, 0.12),
                    0 24px 28px rgba(56, 4, 10, 0.10),
                    -1px 10px 12px rgba(56, 4, 10, 0.14)`
                  : `-6px 40px 30px rgba(56, 4, 10, 0.13),
                    -3px 25px 20px rgba(56, 4, 10, 0.21),
                    -1px 10px 12px rgba(56, 4, 10, 0.25)`,
                paddingBottom: isMobile ? '2px' : undefined,
              }}
            >
              <div style={{ borderRadius: '28px', overflow: 'hidden' }}>{card}</div>
            </div>
          ))}
        </div>
      );
    } else {
      return (
        <SynCardCarousel
          showShadows={!isMobile}
          cards={cards}
          disclaimer={data.disclaimer}
          isMobile={isMobile}
          breakpoints={isPackageCard({ data }) ? packageCardBreakPoints(cards.length) : cardsBreakPoints(cards.length)}
          backgroundColor={data.scrollGradientColor || backgroundColor || 'white-15'}
          layout="four_box_horizontal_scroll"
        />
      );
    }
  } else if (data.layout === 'large_three_box_no_scroll') {
    return (
      <div className="col-span-full grid gap-[24px] md:grid-cols-3">
        {cards.slice(0, 3).map((card, index) => (
          <div
            key={index}
            className="aspect-[448/680] w-full"
            style={{
              borderRadius: '28px',
              overflow: 'hidden',
              boxShadow: `-6px 40px 30px rgba(56, 4, 10, 0.13),
              -3px 25px 20px rgba(56, 4, 10, 0.21),
              -1px 10px 12px rgba(56, 4, 10, 0.25)`,
            }}
          >
            {card}
          </div>
        ))}
      </div>
    );
  } else if (data.layout === 'medium_three_box_no_scroll') {
    return (
      <div className="col-span-full flex flex-col justify-center gap-[24px] md:flex-row">
        {cards.slice(0, 3).map((card, index) => (
          <div
            key={index}
            className="aspect-[448/461] w-full md:max-w-[calc((100%-48px)/3)]"
            style={{
              borderRadius: '28px',
              overflow: 'hidden',
              boxShadow: `-6px 40px 30px rgba(56, 4, 10, 0.13),
              -3px 25px 20px rgba(56, 4, 10, 0.21),
              -1px 10px 12px rgba(56, 4, 10, 0.25)`,
            }}
          >
            {card}
          </div>
        ))}
      </div>
    );
  } else if (data.layout === 'medium_static_grid') {
    return (
      <div className="col-span-full">
        <div className="grid-cols-1: grid gap-24 sm:grid-cols-2 md:grid-cols-4">{cards.map((card) => card)}</div>
      </div>
    );
  } else {
    return (
      <CardCarousel
        showShadows={!isMobile}
        cards={cards}
        disclaimer={data.disclaimer}
        isMobile={isMobile}
        breakpoints={isPackageCard({ data }) ? packageCardBreakPoints(cards.length) : cardsBreakPoints(cards.length)}
        backgroundColor={data.scrollGradientColor || backgroundColor || 'white-15'}
      />
    );
  }
};

const CardCollection = ({
  cardCollectionId,
  headingSize = 'medium',
  backgroundColor = 'white-15',
}: {
  headingSize?: 'xl' | 'large' | 'medium' | 'small' | 'xs';
  headingColor?: string;
  cardCollectionId: string;
  backgroundColor?: string;
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const updateIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);

    return () => {
      window.removeEventListener('resize', updateIsMobile);
    };
  }, []);

  const { loading, error, data } = useQuery<CardCollectionByIdQuery, CardCollectionByIdQueryVariables>(
    CardCollectionById,
    { variables: { id: cardCollectionId } }
  );

  const setIsDoBusinessWithUsForm = useSetAtom(isDoBusinessWithUsFormAtom);
  const openBusinessModal = async () => {
    typewriter.doBusinessWithUsButtonClicked({
      placement: Placement.Footer,
      title: Title.KomduÍViðskipti,
    });
    const contactForm = await fetchContactFormById('2LFEpvaXWCMfNhrjdQTYFg');
    if (contactForm) {
      setIsDoBusinessWithUsForm(true);
    }
    return;
  };

  if (error) {
    Log.error('Error', error);
    return <div className={centeredMainGridClasses} />;
  }

  const item = data?.cardCollectionCollection?.items[0] || null;
  const minHeight = '650px';
  const maxHeight = '100%';

  return (
    <motion.div
      className={`${centeredMainGridClasses}`}
      initial={{ height: minHeight }}
      animate={{ height: loading ? minHeight : maxHeight }}
      transition={{ type: 'tween', ease: 'easeIn', duration: 0.3 }}
    >
      {loading ? (
        <CardCollectionLoading />
      ) : (
        item?.title && (
          <div className="col-span-full">
            {item?.tag && <div className="mb-4">{item.tag}</div>}
            <div className="col-span-full mb-32 grid grid-cols-3 gap-2 px-16 md:px-0">
              <Heading variant={headingSize} classes="text-black-700 col-span-2">
                {item.title}
              </Heading>
              {item?.subtitle && (
                <Subheading small bold classes="text-black-700 col-span-1 self-center">
                  {item.subtitle}
                </Subheading>
              )}
            </div>
          </div>
        )
      )}
      {item && (
        <RenderCarousel
          data={item}
          backgroundColor={backgroundColor}
          callback={openBusinessModal}
          isMobile={isMobile}
        />
      )}
    </motion.div>
  );
};

export default CardCollection;
